import './event-calendar.css';
import cn from 'classnames';
import styles from './event-calendar.module.css';
import { MonthView } from './month-view';
import { TimeGridCalendar } from './time-grid-calendar';
import type { CalendarEvent, CalendarMode, RenderEvent, RenderTimeSlot, Timezones, WeekStartsOn } from './types';

/**
 * Props for the EventCalendar component.
 *
 * @typeParam T - Type of a calendar event. Must extend `CalendarEvent`.
 */
export type EventCalendarProperties<T extends CalendarEvent> = {
  /**
   * Reference date used to determine the visible range.
   */
  date: Date;

  /**
   * Events to display in the calendar.
   */
  events?: T[];

  /**
   * Whether the calendar is loading.
   */
  isLoading?: boolean;

  /**
   * Calendar view mode (e.g. month, week, day).
   */
  mode: CalendarMode;

  /**
   * Callback function to handle the "+ n more" interaction in month view.
   */
  onMoreEventsClick?: (date: Date) => void;

  /**
   * Custom renderer for a single event.
   */
  renderEvent?: RenderEvent<T>;

  /**
   * Custom renderer for a time slot.
   */
  renderTimeSlot?: RenderTimeSlot;

  /**
   * Duration of a time slot in minutes.
   */
  timeSlotDurationInMinutes?: number;

  /**
   * Timezones used for rendering.
   *
   * In month view, only the first timezone is applied.
   */
  timezones: Timezones;

  /**
   * First day of the week (0 = Sunday, 1 = Monday).
   */
  weekStartsOn?: WeekStartsOn;
};

const EventCalendar = <T extends CalendarEvent>({
  date,
  events,
  isLoading,
  mode,
  onMoreEventsClick,
  renderEvent,
  renderTimeSlot,
  timeSlotDurationInMinutes,
  timezones,
  weekStartsOn,
}: EventCalendarProperties<T>) => (
  <div className={cn(styles['event-calendar'], { [styles['event-calendar-month'] ?? '']: mode === 'month' })}>
    {mode === 'month' ? (
      <MonthView<T>
        date={date}
        events={events}
        isLoading={isLoading}
        onMoreEventsClick={onMoreEventsClick}
        renderEvent={renderEvent}
        renderTimeSlot={renderTimeSlot}
        timezones={timezones}
        weekStartsOn={weekStartsOn}
      />
    ) : (
      <TimeGridCalendar<T>
        date={date}
        events={events}
        isLoading={isLoading}
        mode={mode}
        renderEvent={renderEvent}
        renderTimeSlot={renderTimeSlot}
        timeSlotDurationInMinutes={timeSlotDurationInMinutes}
        timezones={timezones}
        weekStartsOn={weekStartsOn}
      />
    )}
  </div>
);

export default EventCalendar;
